import { ChevronDown, ChevronRight, Lock, RefreshCw } from "lucide-react";
import { AUTH_FIELD_META, OAUTH2_AUTH_TYPE } from "./webhookHelpers";
import { inputBaseClass } from "./constants";
import { FormLabel } from "./shared";

const OAUTH_GRID_ROWS = [
  ["token_url", "scopes"],
  ["client_id", "client_secret"]
];

const STORED_MASK = {
  client_id: "•••••••• (stored)",
  client_secret: "•••••••••••••• (stored)"
};

function OAuthFieldInput({
  field,
  config,
  onChange,
  errors,
  required,
  isEdit,
  rotateCreds,
  hasStoredCreds,
  showSecretInputs
}) {
  const meta = AUTH_FIELD_META[field] || { label: field, placeholder: "" };
  const isSecret = meta.secret;
  const inputId = `oauth-${field}-${isEdit ? "edit" : "create"}`;

  if (
    isEdit &&
    hasStoredCreds &&
    !rotateCreds &&
    (field === "client_id" || field === "client_secret")
  ) {
    return (
      <div>
        <FormLabel htmlFor={inputId}>
          {meta.label}{" "}
          <span className="inline-flex items-center gap-0.5 text-gray-500 font-normal">
            <Lock className="w-3 h-3" aria-hidden />
            set
          </span>
        </FormLabel>
        <input
          id={inputId}
          readOnly
          disabled
          className={`${inputBaseClass} bg-gray-100 text-gray-500 cursor-not-allowed`}
          value={STORED_MASK[field]}
        />
      </div>
    );
  }

  if ((field === "client_id" || field === "client_secret") && isEdit && !showSecretInputs) {
    return null;
  }

  const label =
    isEdit && rotateCreds && (field === "client_id" || field === "client_secret")
      ? `New ${meta.label}`
      : meta.label;

  return (
    <div>
      <FormLabel htmlFor={inputId} required={required}>
        {label}
      </FormLabel>
      {meta.hint && !isEdit ? (
        <p className="text-[11px] text-gray-500 mb-1.5 leading-snug">{meta.hint}</p>
      ) : null}
      <input
        id={inputId}
        type={isSecret ? "password" : "text"}
        className={`${inputBaseClass} ${errors[field] ? "border-red-400 ring-red-200" : ""}`}
        value={config[field] ?? ""}
        onChange={(e) => onChange({ ...config, [field]: e.target.value })}
        placeholder={meta.placeholder}
        autoComplete={isSecret ? "new-password" : "off"}
      />
      {errors[field] ? <p className="text-[11px] text-red-600 mt-1">{errors[field]}</p> : null}
    </div>
  );
}

export function WebhookOAuthFields({
  config,
  onChange,
  errors = {},
  requiredFields = [],
  mode = "create",
  hasStoredCreds = false,
  rotateCreds = false,
  onRotateToggle
}) {
  const isEdit = mode === "edit";
  const showSecretInputs = !isEdit || rotateCreds;
  const isRequired = (field) => requiredFields.includes(field);

  const panelClass =
    mode === "create"
      ? "mt-4 border-gray-200 bg-[#f8f9fb]"
      : "border-gray-200 bg-[#f8f9fb]";

  const handleRotateToggle = () => onRotateToggle?.(!rotateCreds);

  return (
    <div className={`rounded-lg border p-5 ${panelClass} ${isEdit ? "" : "mt-4"}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-4">
        OAuth2 Config
      </p>

      <div className="space-y-4">
        {OAUTH_GRID_ROWS.map((row) => (
          <div key={row.join("-")} className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            {row.map((field) =>
              requiredFields.includes(field) || (isEdit && field in AUTH_FIELD_META) ? (
                <OAuthFieldInput
                  key={field}
                  field={field}
                  config={config}
                  onChange={onChange}
                  errors={errors}
                  required={
                    isRequired(field) ||
                    (isEdit && rotateCreds && (field === "client_id" || field === "client_secret"))
                  }
                  isEdit={isEdit}
                  rotateCreds={rotateCreds}
                  hasStoredCreds={hasStoredCreds}
                  showSecretInputs={showSecretInputs}
                />
              ) : null
            )}
          </div>
        ))}
      </div>

      {isEdit && hasStoredCreds ? (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-[11px] text-gray-500 mb-2">
            Toggle &quot;Rotate credentials&quot; below to replace.
          </p>
          <button
            type="button"
            onClick={handleRotateToggle}
            className="flex items-center gap-2 w-full text-left text-sm font-medium text-gray-800 hover:text-gray-900 py-1"
          >
            {rotateCreds ? (
              <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
            )}
            <RefreshCw className="w-4 h-4 text-gray-500 shrink-0" />
            Rotate credentials
          </button>
          {rotateCreds ? (
            <p className="text-[11px] text-gray-500 mt-3 leading-relaxed pl-6">
              On save, both old and new credential versions exist in Secret Manager briefly; the
              dispatcher picks up the new version on its next token fetch. No outage window for
              in-flight requests.
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === "create" ? (
        <p className="text-[11px] text-gray-500 flex items-start gap-1.5 mt-4 pt-4 border-t border-gray-200 leading-relaxed">
          <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" aria-hidden />
          Credentials are written to Google Secret Manager on save and never sent back to the
          browser. The row stores only an opaque pointer.
        </p>
      ) : null}
    </div>
  );
}

export function getOAuthRequiredFields(integrations, authType) {
  const match = integrations?.auth_types?.find((a) => a.value === authType);
  return match?.required_auth_config_fields ?? [];
}

export function isOAuthAuthType(authType) {
  return authType === OAUTH2_AUTH_TYPE;
}
